import moment from 'moment'

/**
 * Utility functions for processing lab results with the new schema
 * Schema: {
 *   "error": "",
 *   "batchNo": "7219592",
 *   "latestResult": "25/06/2025 14:36:13",
 *   "hasMoreResults": false,
 *   "allSpecimensComplete": true,
 *   "pathologyReports": { "Encoding": "base64", "xml": "base64 XML" },
 *   "pathologyReportPDFs": [{ "messageId": 1, "pdf": "base64 PDF" }]
 * }
 */

/**
 * Convert base64 string to blob for PDF handling
 * @param {string} base64Data - Base64 encoded PDF data
 * @param {string} type - MIME type (default: 'application/pdf')
 * @returns {Blob} - Blob object for the PDF
 */
export const base64ToBlob = (base64Data, type = 'application/pdf') => {
  try {
    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type })
  } catch (error) {
    console.error('Failed to convert base64 to blob:', error)
    throw new Error('Invalid base64 data')
  }
}

/**
 * Download a PDF from base64 data
 * @param {string} base64Data - Base64 encoded PDF data
 * @param {string} filename - Name for the downloaded file
 * @returns {Promise<void>}
 */
export const downloadPDFFromBase64 = async (base64Data, filename = 'lab-results.pdf') => {
  try {
    const blob = base64ToBlob(base64Data)
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  } catch (error) {
    console.error('Failed to download PDF:', error)
    throw new Error('Failed to download PDF file')
  }
}

/**
 * Validate lab result schema
 * @param {Object} result - Result object to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateResultSchema = (result) => {
  const errors = []

  if (!result || typeof result !== 'object') {
    errors.push('Result must be an object')
    return { isValid: false, errors }
  }

  // Check required fields
  const requiredFields = ['batchNo', 'latestResult', 'hasMoreResults', 'allSpecimensComplete']
  requiredFields.forEach((field) => {
    if (!(field in result)) {
      errors.push(`Missing required field: ${field}`)
    }
  })

  // Validate date format for latestResult
  if (result.latestResult && !moment(result.latestResult, 'DD/MM/YYYY HH:mm:ss', true).isValid()) {
    errors.push('latestResult must be in format DD/MM/YYYY HH:mm:ss')
  }

  // Validate boolean fields
  if (typeof result.hasMoreResults !== 'boolean') {
    errors.push('hasMoreResults must be a boolean')
  }

  if (typeof result.allSpecimensComplete !== 'boolean') {
    errors.push('allSpecimensComplete must be a boolean')
  }

  // Validate pathologyReportPDFs array
  if (result.pathologyReportPDFs && !Array.isArray(result.pathologyReportPDFs)) {
    errors.push('pathologyReportPDFs must be an array')
  } else if (result.pathologyReportPDFs) {
    result.pathologyReportPDFs.forEach((pdf, index) => {
      if (!pdf.messageId || !pdf.pdf) {
        errors.push(`pathologyReportPDFs[${index}] must have messageId and pdf fields`)
      }
    })
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Format date from the lab result format to display format
 * @param {string} dateString - Date in format DD/MM/YYYY HH:mm:ss
 * @param {string} outputFormat - Desired output format (default: 'YYYY-MM-DD HH:mm')
 * @returns {string} - Formatted date string
 */
export const formatLabDate = (dateString, outputFormat = 'YYYY-MM-DD HH:mm') => {
  try {
    if (!dateString) return 'N/A'
    const parsedDate = moment(dateString, 'DD/MM/YYYY HH:mm:ss')
    return parsedDate.isValid() ? parsedDate.format(outputFormat) : 'Invalid Date'
  } catch (error) {
    console.error('Failed to format date:', error)
    return 'Invalid Date'
  }
}

/**
 * Generate filename for PDF download
 * @param {string} orderNumber - Lab order number
 * @param {number} messageId - PDF message ID
 * @param {string} prefix - Optional prefix (default: 'lab-report')
 * @returns {string} - Generated filename
 */
export const generatePDFFilename = (orderNumber, messageId, prefix = 'lab-report') => {
  const timestamp = moment().format('YYYY-MM-DD')
  return `${prefix}-${orderNumber}-${messageId}-${timestamp}.pdf`
}

export default {
  base64ToBlob,
  downloadPDFFromBase64,
  validateResultSchema,
  formatLabDate,
  generatePDFFilename
}
